import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';

import { Subscription } from 'rxjs';

import { ImageService, ImageNodeAttachmentMetadata } from '../../_service/image.service';
import { FileUploadService } from '../../_service/file-upload.service';
import { AttachmentImageInsertPayload, LinkedImageInsertPayload } from './extensions/phase1-image';
import { validateDroppedImage } from './extensions/image-drop';

export type ImageInsertMode = 'url' | 'upload' | 'existing';

interface ExistingImageOption {
  meta: ImageNodeAttachmentMetadata;
  previewUrl: string | null;
}

@Component({
  selector: 'app-image-insert-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, DialogModule, InputTextModule, ButtonModule],
  template: `
    <p-dialog
      header="Insert Image"
      [(visible)]="visible"
      [modal]="true"
      [closable]="true"
      [draggable]="false"
      [resizable]="false"
      (onHide)="onDialogHide()"
      [style]="{ width: '480px', maxWidth: '92vw' }"
      appendTo="body">

      <!-- Mode selector -->
      <div class="flex gap-2 mb-4 border-b border-surface-200 pb-3">
        <button
          type="button"
          class="px-3 py-1.5 rounded text-sm font-medium transition-colors"
          [class.bg-primary-600]="mode === 'url'"
          [class.text-white]="mode === 'url'"
          [class.bg-surface-100]="mode !== 'url'"
          [class.text-surface-700]="mode !== 'url'"
          (click)="setMode('url')">
          From URL
        </button>
        <button
          type="button"
          class="px-3 py-1.5 rounded text-sm font-medium transition-colors"
          [class.bg-primary-600]="mode === 'upload'"
          [class.text-white]="mode === 'upload'"
          [class.bg-surface-100]="mode !== 'upload'"
          [class.text-surface-700]="mode !== 'upload'"
          (click)="setMode('upload')">
          Upload
        </button>
        <button
          type="button"
          class="px-3 py-1.5 rounded text-sm font-medium transition-colors"
          [class.bg-primary-600]="mode === 'existing'"
          [class.text-white]="mode === 'existing'"
          [class.bg-surface-100]="mode !== 'existing'"
          [class.text-surface-700]="mode !== 'existing'"
          (click)="setMode('existing')">
          From this note
        </button>
      </div>

      <!-- URL mode -->
      <div *ngIf="mode === 'url'" class="flex flex-col gap-3">
        <div class="flex flex-col gap-1">
          <label for="img-url" class="text-sm font-semibold">Image URL</label>
          <input
            id="img-url"
            pInputText
            type="url"
            [(ngModel)]="imageUrl"
            placeholder="https://example.com/image.png"
            class="w-full"
            autofocus />
        </div>
        <div *ngIf="urlError" class="text-sm text-red-600">{{ urlError }}</div>
        <div *ngIf="urlProbing" class="text-sm text-surface-600 flex items-center gap-2">
          <span class="material-icons text-base animate-spin">sync</span>
          Loading image…
        </div>
      </div>

      <!-- Upload mode -->
      <div *ngIf="mode === 'upload'" class="flex flex-col gap-3">
        <div *ngIf="!noteId" class="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          Upload is only available when editing a saved note.
        </div>
        <div *ngIf="noteId" class="flex flex-col gap-2">
          <label class="text-sm font-semibold">Select image file</label>
          <input
            #fileInput
            type="file"
            accept="image/png,image/jpeg"
            class="block w-full text-sm text-surface-700 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
            (change)="onFileSelected($event)" />
          <div *ngIf="selectedFile" class="text-xs text-surface-500">
            {{ selectedFile.name }} ({{ formatBytes(selectedFile.size) }})
          </div>
        </div>
        <div *ngIf="uploadError" class="text-sm text-red-600">{{ uploadError }}</div>
        <div *ngIf="uploading" class="text-sm text-surface-600 flex items-center gap-2">
          <span class="material-icons text-base animate-spin">sync</span>
          Uploading...
        </div>
      </div>

      <!-- Existing attachment mode: reuse an image already attached to this note -->
      <div *ngIf="mode === 'existing'" class="flex flex-col gap-3">
        <div *ngIf="!noteId" class="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          Only available when editing a saved note.
        </div>
        <div *ngIf="noteId && existingLoading" class="text-sm text-surface-600 flex items-center gap-2">
          <span class="pi pi-spin pi-spinner"></span>
          Loading images…
        </div>
        <div *ngIf="noteId && !existingLoading && existingImages.length === 0" class="text-sm text-surface-600">
          This note has no image attachments yet.
        </div>
        <div
          *ngIf="noteId && !existingLoading && existingImages.length > 0"
          class="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[50vh] overflow-y-auto p-0.5"
          role="listbox"
          aria-label="Images attached to this note">
          <button
            *ngFor="let option of existingImages"
            type="button"
            role="option"
            [attr.aria-selected]="selectedExisting === option"
            [title]="option.meta.filename || option.meta.attachmentId"
            class="flex flex-col items-stretch gap-1 rounded border p-1 text-left min-h-[44px] transition-colors hover:bg-(--p-content-hover-background)"
            [class.border-primary-500]="selectedExisting === option"
            [class.ring-2]="selectedExisting === option"
            [class.ring-primary-500]="selectedExisting === option"
            [class.border-surface-200]="selectedExisting !== option"
            (click)="selectedExisting = option"
            (dblclick)="selectedExisting = option; onInsertExisting()">
            <span class="flex h-16 items-center justify-center overflow-hidden rounded bg-surface-100">
              <img *ngIf="option.previewUrl" [src]="option.previewUrl" alt="" class="max-h-16 max-w-full object-contain" />
              <span *ngIf="!option.previewUrl" class="pi pi-image text-surface-400"></span>
            </span>
            <span class="truncate text-xs text-(--p-text-color)">{{ option.meta.filename || 'Image' }}</span>
          </button>
        </div>
      </div>

      <ng-template pTemplate="footer">
        <div class="flex gap-2 justify-end">
          <p-button
            label="Cancel"
            severity="secondary"
            (onClick)="onCancel()">
          </p-button>
          <p-button
            *ngIf="mode === 'url'"
            label="Insert"
            [disabled]="!imageUrl.trim() || urlProbing"
            [loading]="urlProbing"
            (onClick)="onInsertUrl()">
          </p-button>
          <p-button
            *ngIf="mode === 'upload'"
            label="Upload & Insert"
            [disabled]="!selectedFile || !noteId || uploading || !!uploadError"
            [loading]="uploading"
            (onClick)="onUpload()">
          </p-button>
          <p-button
            *ngIf="mode === 'existing'"
            label="Insert"
            [disabled]="!selectedExisting || !noteId"
            (onClick)="onInsertExisting()">
          </p-button>
        </div>
      </ng-template>
    </p-dialog>
  `,
})
export class ImageInsertDialogComponent implements OnChanges, OnDestroy {
  @Input() visible = false;
  @Input() noteId: string | null = null;
  @Input() spaceId?: string;

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() insertLinked = new EventEmitter<LinkedImageInsertPayload>();
  @Output() insertAttachment = new EventEmitter<AttachmentImageInsertPayload>();
  @Output() cancel = new EventEmitter<void>();

  mode: ImageInsertMode = 'url';
  imageUrl = '';
  urlError = '';
  urlProbing = false;

  selectedFile: File | null = null;
  uploading = false;
  uploadError = '';

  existingImages: ExistingImageOption[] = [];
  existingLoading = false;
  selectedExisting: ExistingImageOption | null = null;
  private existingSub?: Subscription;
  private previewSubs: Subscription[] = [];

  constructor(
    private imageService: ImageService,
    private fileUpload: FileUploadService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true) {
      this.reset();
    }
  }

  ngOnDestroy(): void {
    this.clearExistingImages();
  }

  setMode(m: ImageInsertMode): void {
    this.mode = m;
    this.urlError = '';
    this.uploadError = '';
    if (m === 'existing') {
      this.loadExistingImages();
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedFile = input.files?.[0] ?? null;
    // Same rules as drag & drop (PNG/JPEG, 10 MB), checked before anything is stored.
    this.uploadError = this.selectedFile ? validateDroppedImage(this.selectedFile) ?? '' : '';
  }

  /** Inserts another reference to an image already attached to this note (no new upload). */
  onInsertExisting(): void {
    const meta = this.selectedExisting?.meta;
    if (!meta) {
      return;
    }
    this.insertAttachment.emit({
      attachmentId: meta.attachmentId,
      src: meta.src,
      filename: meta.filename,
      fileSizeBytes: meta.fileSizeBytes,
      mimeType: meta.mimeType,
    });
    this.closeDialog();
  }

  private loadExistingImages(): void {
    this.clearExistingImages();
    if (!this.noteId) {
      return;
    }
    const noteId = this.noteId;
    this.existingLoading = true;
    this.existingSub = this.imageService.getNoteImages(noteId, this.spaceId ?? undefined).subscribe((images) => {
      this.existingLoading = false;
      this.existingImages = images.map((meta) => ({ meta, previewUrl: null }));
      for (const option of this.existingImages) {
        // Authenticated fetch (cache-first), like the editor's own image hydration.
        this.previewSubs.push(
          this.fileUpload.getFileForNote(noteId, option.meta.attachmentId, option.meta.filename, this.spaceId ?? undefined).subscribe({
            next: (blob) => (option.previewUrl = URL.createObjectURL(blob)),
            error: () => undefined,
          }),
        );
      }
    });
  }

  private clearExistingImages(): void {
    this.existingSub?.unsubscribe();
    this.previewSubs.forEach((sub) => sub.unsubscribe());
    this.previewSubs = [];
    this.existingImages.forEach((option) => option.previewUrl && URL.revokeObjectURL(option.previewUrl));
    this.existingImages = [];
    this.selectedExisting = null;
    this.existingLoading = false;
  }

  onInsertUrl(): void {
    const url = this.imageUrl.trim();
    if (!url) {
      this.urlError = 'Please enter a URL.';
      return;
    }
    try {
      new URL(url);
    } catch {
      this.urlError = 'Please enter a valid URL.';
      return;
    }

    this.urlError = '';
    this.urlProbing = true;

    // First try CORS-enabled probing; if that fails, retry a plain load so users
    // can still insert displayable cross-origin images.
    this.probeLinkedImage(url, true)
      .catch(() => this.probeLinkedImage(url, false))
      .then(({ naturalWidth, naturalHeight }) => {
        this.urlProbing = false;
        const { width, height } = this.computeInitialDisplaySize(naturalWidth, naturalHeight);
        this.insertLinked.emit({
          url,
          naturalWidth,
          naturalHeight,
          width,
          height,
        });
        this.closeDialog();
      })
      .catch(() => {
        this.urlProbing = false;
        this.urlError = 'Could not load image. The host may block access or be temporarily unavailable.';
      });
  }

  private probeLinkedImage(url: string, useCorsAnonymous: boolean): Promise<{ naturalWidth: number | null; naturalHeight: number | null }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      if (useCorsAnonymous) {
        img.crossOrigin = 'anonymous';
      }

      img.onload = () => {
        const nw = img.naturalWidth > 0 ? img.naturalWidth : null;
        const nh = img.naturalHeight > 0 ? img.naturalHeight : null;
        resolve({ naturalWidth: nw, naturalHeight: nh });
      };

      img.onerror = () => {
        reject(new Error('Image load failed'));
      };

      img.src = url;
    });
  }

  private computeInitialDisplaySize(
    naturalWidth: number | null,
    naturalHeight: number | null,
  ): { width: number | null; height: number | null } {
    const MAX_DISPLAY_WIDTH = 800;
    const nw = naturalWidth ?? 0;
    const nh = naturalHeight ?? 0;

    if (nw <= 0 || nh <= 0) {
      return { width: null, height: null };
    }

    if (nw > MAX_DISPLAY_WIDTH) {
      return {
        width: MAX_DISPLAY_WIDTH,
        height: Math.round((nh / nw) * MAX_DISPLAY_WIDTH),
      };
    }

    return { width: nw, height: nh };
  }

  onUpload(): void {
    if (!this.selectedFile || !this.noteId) {
      return;
    }
    const invalid = validateDroppedImage(this.selectedFile);
    if (invalid) {
      this.uploadError = invalid;
      return;
    }
    this.uploading = true;
    this.uploadError = '';
    this.imageService.uploadImage(this.selectedFile, this.noteId, this.spaceId ?? undefined).subscribe({
      next: (meta: ImageNodeAttachmentMetadata) => {
        this.uploading = false;
        this.insertAttachment.emit({
          attachmentId: meta.attachmentId,
          src: meta.src,
          filename: meta.filename ?? this.selectedFile?.name,
          fileSizeBytes: meta.fileSizeBytes ?? this.selectedFile?.size,
          mimeType: meta.mimeType ?? this.selectedFile?.type,
        });
        this.closeDialog();
      },
      error: (err: unknown) => {
        this.uploading = false;
        this.uploadError = err instanceof Error ? err.message : 'Upload failed.';
      },
    });
  }

  onCancel(): void {
    this.cancel.emit();
    this.closeDialog();
  }

  onDialogHide(): void {
    if (this.visible) {
      this.visibleChange.emit(false);
    }
  }

  formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private closeDialog(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  private reset(): void {
    this.mode = 'url';
    this.imageUrl = '';
    this.urlError = '';
    this.urlProbing = false;
    this.selectedFile = null;
    this.uploading = false;
    this.uploadError = '';
    this.clearExistingImages();
  }
}
